/** A session: its registry entry, run status and document shape. */

import type { SessionStripActivity } from '../../log/strip-activity'
import type { LogDoc } from '../../log/types'
import type { SessionCheckout, SessionCodeRef } from '../code/types'
import type { StoredText } from '../stored-string'
import type { Allowance } from './allowance'

/** One entry in the project doc's session registry (read by `useSessionList`). */
export interface SessionMetaEntry {
  title?: StoredText
  updatedAt?: number
  /**
   * When someone last SENT on this session, written by `touchSession` and by
   * nothing else.
   *
   * `updatedAt` says the session was used, and a rename counts as use: it has
   * to, the host window is ordered by it. But the daemon's ingest watch needs a
   * clock that moves ONLY when the session's own log should have grown. It
   * compared `updatedAt` against the log's newest entry, so every rename read
   * as a send whose run never arrived, and four minutes later the daemon tore
   * its round down, then restarted, on a loop for half an hour, over a session
   * nobody had sent anything to. Absent on registries written before this
   * field, which the watch reads as nothing to compare.
   */
  sentAt?: number
  /** Stable id of the external source this session was imported from (e.g. a
   *  captured conversation). Lets an importer dedup by reading only this registry,
   *  without hydrating any session doc. */
  sourceId?: string
  /** How many source turns have been imported, so a watcher can append only the
   *  delta as the source grows (incremental capture). */
  sourceTurns?: number
  /**
   * When this session was filed as done (RFC 0286): from the session list's
   * row menu, or by an agent with `complete_session` because a person asked.
   *
   * Whether someone is finished with a session is the one thing no projection
   * can recover, which is why it is stored rather than derived. A timestamp
   * rather than a flag: it sorts, and it says when.
   *
   * Absent means not done, so every session written before this field reads
   * correctly.
   */
  completedAt?: number
  /**
   * What this session is DOING, mirrored from its log (RFC 0284 S8a).
   *
   * THIS FIELD USED TO BE REFUSED, and the objection was right: a stored status
   * is a mirror of run state that goes wrong the moment a daemon dies mid-run
   * and leaves a session claiming to work forever. Every value except
   * `completedAt` was therefore projected from the session's own log.
   *
   * The cost of that was a document hydration per row. `useSessionStatuses`
   * opens every session it reports on, which is why it has a cap of twenty and
   * why its own header records taking a renderer past its heap limit before the
   * list had finished drawing. It also means no INSTALLED sidebar or header can
   * say which session is waiting for you, because a fill cannot pay
   * that price and should not be handed a capability that does.
   *
   * `statusAt` is what makes the mirror safe, and it is the presence model
   * (`isDevicePresent`) applied to a run: a claim is believed only while it is
   * FRESH. A daemon that dies mid-run stops refreshing, its `running` ages out,
   * and the row reads as unknown rather than as working forever. Nothing has to
   * notice the death or clean up after it.
   *
   * Absent means the runtime does not write this, which reads as UNKNOWN and
   * never as idle: a session nobody has reported on is not a quiet one, and
   * reporting it as quiet is how a stuck session gets skipped.
   */
  status?: SessionRunStatus
  /** When `status` was last written. A non-idle status older than
   *  `STATUS_FRESH_MS` is stale and reads as unknown. */
  statusAt?: number
  /**
   * The session that OPENED this one, when an agent did.
   *
   * Written by `start_session` and by nothing else. A session a person created
   * has no parent, and neither does one written before this field existed, so
   * absent means top level and every registry already on disk reads correctly.
   *
   * ON THE REGISTRY, not on the session document, because the list is what
   * needs it and the list is the one read that must stay zero-hydration
   * (RFC 0233). Asking "is this a sub-session" by opening the session doc would
   * pay a document per row to answer a question about the row.
   *
   * The id is not resolved here and may name a session in ANOTHER project, or
   * one since deleted. A reader that cannot find the parent shows the session as
   * top level rather than hiding it: a sub-session whose only door has been
   * removed must not become unreachable.
   */
  parentSessionId?: string
  /**
   * The branch this session works on, mirrored from `SessionDoc.code.branch`
   * (RFC 0277 S12) so the sidebar can say which worktree each session is in
   * without opening every session. The session doc stays the truth; absent
   * means not mirrored yet, not "no branch".
   */
  branch?: string
  /**
   * Who wrote `title` (RFC 0311 3b), so a generated name never replaces a
   * person's.
   *
   * `'stub'` is the placeholder a session is born with (the first line typed,
   * or "Session 3"), and it is the ONLY value the agent's one-time rename
   * replaces. Absent reads as `'you'`: a title written before this field
   * existed may have been typed by someone, and renaming it would be the one
   * mistake this field exists to prevent. Read it through `titleSetBy()`.
   */
  titleSetBy?: SessionTitleSetBy
  /**
   * The documents this session holds, most recent first, at most
   * `HOLDING_MAX` (RFC 0311 3d).
   *
   * A MIRROR of `projectHoldings`, not a second record. The session's own log
   * stays the truth; this is what a document header reads to say who is in the
   * document without opening every session in the project. Written only by
   * `setSessionHolding`, at run transitions, so it can lag the log by a turn.
   */
  holding?: string[]
  /**
   * The name an agent gave a stub session (RFC 0311 3b): the namer or the
   * agent's own `name_session`.
   *
   * ITS OWN FIELD, NOT `title`. A person's rename writes `title` and
   * `titleSetBy`; if the agent wrote those same two keys, a rename made while
   * the agent's write was still syncing would merge key by key, and the
   * person's title could lose. With the agent on a key nobody else writes,
   * the merge cannot mix them. Read the title through `sessionTitleOf`.
   */
  agentTitle?: string
  /** When the namer asked the model (RFC 0311 3b), so a restart or re-host
   *  does not ask again for a session it already tried and failed on. */
  nameTriedAt?: number
}

/** How many held documents the registry mirrors per session. */
export const HOLDING_MAX = 8

/** 'stub' the typed-text fallback, 'agent' a generated or agent-chosen name, 'you' a person's. */
export type SessionTitleSetBy = 'stub' | 'agent' | 'you'

/** Bounded content for the compact composer, kept on the session document. */
export type { SessionStripActivity } from '../../log/strip-activity'
/**
 * What a session is doing, as the registry stores it and a list draws it.
 *
 * Declared here rather than imported from the log projection, because a doc
 * shape describing what is ON a document should not depend on how the same
 * words are computed from somewhere else. `projectSessionStatus` produces the
 * same four.
 */
export type SessionRunStatus = 'idle' | 'queued' | 'running' | 'asking'
/**
 * Who serves inference for a project (RFC 0261; searugast `InferenceMode`).
 *
 * `external` = served by the deployment's shared inference worker.
 * `session`  = served in-process by whichever interactive session opened it.
 *
 * A PLAIN STRING on purpose: searugast stores it as one so a JS peer can write
 * the field directly, which is exactly what mod is. Readers must treat anything
 * that is not literally `external` as not-external — see `isExternallyServed`.
 */
export type InferenceMode = 'external' | 'session'
/**
 * The session document. Carries its own message log inline under `thread`
 * (THREAD_KEY) — there is no separate log doc; `BranchLog` wraps the session
 * handle and reads/writes `thread`.
 */
/** RFC 0345 3e: one agent key of a session. */
export interface SessionKeyRecord {
  /** The person group (hex) that delegated the key: "this agent is Nassar's". */
  person: string
  createdAt: number
  revokedAt?: number
  /** RFC 0345 grid 8: a granted turn's key, used only when this person's
   * agent answers the named collaborator. Never offered as an agent to pick;
   * a label, the host's private record decides. */
  grantFor?: string
  /** When that grant ends (7 days by default). */
  expiresAt?: number
}

export interface SessionDoc {
  /** RFC 0345: direct signed owner, checked before granting session access. */
  sharingGroupId?: string
  /** Documents explicitly granted to this session, never read-only holdings. */
  sharedDocumentIds?: string[]
  /** RFC 0345 3e: the agent keys this session has, by key id (hex), so a
   *  member can see whose agent a write came from. A pointer only: access is
   *  the signed grants, read again before any revocation. */
  agentKeys?: Record<string, SessionKeyRecord>
  /** RFC 0345 3c: allowances scoped to this session, visible to its members. */
  allowances?: Allowance[]
  /** RFC 0345 3c: one receipt per document the session's agent wrote, each
   *  signed by its session key. Members can delete one but cannot make one;
   *  sharing/document-share.ts verifies them. */
  documentWrites?: {
    documentId: string
    heads: string[]
    key: string
    signature: string
  }[]
  /** RFC 0355: source this session runs in, including a plain folder. */
  codeSourceId?: string
  id: string
  title: string
  createdAt: number
  updatedAt: number
  status: 'active' | 'review' | 'complete'
  /** Stable source id when imported (e.g. a captured conversation's id). */
  sourceId?: string
  /** RFC 0348 3h: sessions following this session's stops. Each value is the
   * events it hears, flat as `ended,asked`; removing a key unsubscribes it. */
  stopSubscriptions?: Record<string, string>
  /** Root-of-trust for this session (the creating project/identity). The
   *  sync-server access gate keys off it: a session without a `createdBy` has no
   *  creator to evaluate participants against, so the gate falls open. Stamp it at
   *  create so per-session sync access is enforceable. */
  createdBy?: string
  /** RFC 0215 entry-DAG; initialised at create, appended to by `appendMessage`. */
  thread?: LogDoc
  /**
   * Latest bounded reply or pending question (RFC 0304 3h). Written at the log
   * boundary so the compact composer can read it without projecting `thread`.
   */
  stripActivity?: SessionStripActivity
  /** The current assistant message while it streams. A compact strip follows
   * this one entry directly rather than decoding the historical transcript. */
  stripReplyMessageId?: string
  /** RFC 0264: the code this session works on. A session works on a branch of the
   *  project trunk (fork or attach); this records which branch and where its head
   *  sits, so "review this branch" reads a pointer, not a tree. Absent for a session
   *  that never touched code. */
  code?: SessionCodeRef
  /** RFC 0362 3b: the sources this session has branched, keyed by project id
   *  then source id. A source with no entry is its shared checkout. Read with
   *  `sessionCheckouts`, which also reads an older session's single `code`. */
  checkouts?: Record<string, Record<string, SessionCheckout>>
  /** RFC 0362 3c: projects this session works in besides its home, in the
   *  order added. The home is never in this list, and adding one never
   *  changes who can open the session. */
  addedProjectIds?: string[]
  /** Comments on this session and its messages (RFC 0339 3c), the same
   *  `CommentsDoc` a page links. Absent until the first one. */
  commentsId?: string
}
